import config, {Brand} from '../../config';

import {FIELD_ERROR_COLOR} from './colour';

/**
 * Text-field chrome palette: border, surface, text colour and border width per
 * visual state. `fieldChrome()` in app/design-system/form/FormField.tsx reads
 * it, and so do the Otp boxes through `fieldChrome()`.
 *
 * The default palette is the LMG UI Library "Field States" frame
 * (file JwxvfWAYFSi5JBQ67QYxOG, node 444:6223). It is not the brand primary:
 * SipnSave's primary #FFDD00 is about 1.35:1 on the white field surface.
 *
 * A brand overrides states in `overridesByBrand`. Every state it leaves out
 * keeps the default palette's value. Only Thirsty Camel overrides (DIS-1825).
 */

export type FieldStateChrome = {
  borderColor: string;
  backgroundColor: string;
  color: string;
  borderWidth: number;
};

export type FieldPalette = {
  default: FieldStateChrome;
  focus: FieldStateChrome;
  filled: FieldStateChrome;
  error: FieldStateChrome;
  disabled: FieldStateChrome;
  /**
   * Web hover. Undefined means hover does not change the chrome, which is the
   * LMG UI Library default. Hover never fires on native.
   */
  hover?: FieldStateChrome;
  /**
   * Whether the Otp boxes draw the focus state. False keeps the Otp boxes on
   * `default` and `filled` only, as they were before DIS-1825.
   */
  otpFocus: boolean;
};

/**
 * The border width every state is laid out for. A state with a wider border
 * takes the difference back as a negative margin, so neither the field's
 * footprint nor its content moves when the width changes.
 */
export const BASE_FIELD_BORDER_WIDTH = 1;

// SHORTCUT: field chrome held as literals. Ceiling: no L0 colour token layer
// exists, so this file is the one place that configures field chrome per brand.
// The default values are LMG UI Library Field States values and must NOT be
// repointed at the per-brand theme primary (see the note above). Upgrade path:
// a design-system colour module under app/design-system/tokens/ - DIS-1737
// blocks it, DIS-1735 is the epic.
const FIELD_TEXT = '#000000';
const FIELD_SURFACE = '#FFFFFF';
const FILLED_BACKGROUND = '#F6FBFF';

export const FIELD_ICON_COLOR = '#666666';
export const FIELD_PLACEHOLDER_COLOR = '#B8B8B8';

export const DEFAULT_FIELD_PALETTE: FieldPalette = {
  default: {
    // DIS-1854: #979797 on white is 2.92:1, under the 3:1 non-text minimum.
    // #757575 clears it. Kept as a field literal so universal.greyDark can
    // still change nav, search placeholder and the checkbox without moving fields.
    borderColor: '#757575',
    backgroundColor: FIELD_SURFACE,
    color: FIELD_TEXT,
    borderWidth: 1,
  },
  focus: {
    borderColor: '#0054A6',
    backgroundColor: FIELD_SURFACE,
    color: FIELD_TEXT,
    borderWidth: 1,
  },
  filled: {
    borderColor: '#3E7EBC',
    backgroundColor: FILLED_BACKGROUND,
    color: FIELD_TEXT,
    borderWidth: 1,
  },
  error: {
    borderColor: FIELD_ERROR_COLOR,
    backgroundColor: FILLED_BACKGROUND,
    color: FIELD_TEXT,
    borderWidth: 1,
  },
  disabled: {
    borderColor: '#E0E0E0',
    backgroundColor: '#EFEFEF',
    color: '#808080',
    borderWidth: 1,
  },
  otpFocus: false,
};

// DIS-1825: Thirsty Camel green. #94D600 on #FFFFFF is about 1.8:1, below the
// 3:1 the default palette's focus border meets. The product owner accepted it
// for Thirsty Camel on DIS-1825; fieldChrome.contrast.test.ts pins the value.
const TC_GREEN = '#94D600';
const TC_FOCUS: FieldStateChrome = {
  borderColor: TC_GREEN,
  backgroundColor: FIELD_SURFACE,
  color: FIELD_TEXT,
  borderWidth: 2,
};

const overridesByBrand: Partial<Record<Brand, Partial<FieldPalette>>> = {
  [Brand.THIRSTY_CAMEL]: {
    focus: TC_FOCUS,
    hover: TC_FOCUS,
    filled: {
      borderColor: TC_GREEN,
      backgroundColor: '#F6FFEC',
      color: FIELD_TEXT,
      borderWidth: 1,
    },
    otpFocus: true,
  },
};

export function fieldPaletteFor(brand: Brand | string | undefined): FieldPalette {
  return {...DEFAULT_FIELD_PALETTE, ...overridesByBrand[brand as Brand]};
}

export const fieldPalette: FieldPalette = fieldPaletteFor(config.BASE_TENANT);
